import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import ModuleEditorBase from './ModuleEditorBase';
import { isPending } from './contentPlaceholder';

interface VideoMeetingEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: {
      meeting_url?: string;
      text?: string;
    };
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: any) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

function LinkIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="text-primary-500"
    >
      <path
        d="M18.375 12.739l-4.596 4.596a4.5 4.5 0 11-6.364-6.364l4.596-4.596M9.879 14.121l4.243-4.243M5.636 18.364a4.5 4.5 0 010-6.364l4.596-4.596a4.5 4.5 0 116.364 6.364l-4.596 4.596"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TextIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="text-primary-500"
    >
      <path
        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Treat the pending sentinel as empty for display purposes. */
function displayValue(raw: string | undefined): string {
  if (!raw || isPending(raw)) return '';
  return raw;
}

export default function VideoMeetingEditor({ module, onUpdate, onDelete }: VideoMeetingEditorProps) {
  const [url, setUrl] = useState(displayValue(module.details.meeting_url));
  const [text, setText] = useState(module.details.text || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setUrl(displayValue(module.details.meeting_url));
    setText(module.details.text || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id]);

  useEffect(() => {
    const sanitizedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    const storedUrl = displayValue(module.details.meeting_url);

    if (url !== storedUrl || sanitizedText !== (module.details.text || '')) {
      // Backend requires meeting_url to be ≥ 1 char — skip autosave while the
      // user hasn't entered anything yet (keeps the module alive with its
      // seeded placeholder instead of triggering a 400 on every keystroke).
      if (url.trim().length === 0) return;

      const timeout = setTimeout(async () => {
        try {
          setIsSaving(true);
          setIsSaved(false);
          await onUpdate(module.id, {
            details: { meeting_url: url, text: sanitizedText },
          });
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        } catch (error) {
          console.error('Failed to save meeting URL:', error);
        } finally {
          setIsSaving(false);
        }
      }, 1200);

      return () => clearTimeout(timeout);
    }
  }, [url, text, module.id, module.details.meeting_url, module.details.text, onUpdate]);

  return (
    <ModuleEditorBase
      moduleType={module.type}
      moduleTitle={module.title}
      onTitleChange={async (title) => await onUpdate(module.id, { title })}
      onDelete={async () => await onDelete(module.id)}
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <div
        className="rounded-2xl bg-surface-primary p-6"
        style={{ boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.1)' }}
      >
        <h3 className="text-lg font-semibold text-text-primary mb-2">Add URL</h3>
        <p className="text-[13px] leading-5 text-text-secondary mb-4">
          Some websites don&rsquo;t allow being embedded inside other apps — learners
          always get an <span className="font-semibold text-text-primary">Open in new tab</span>{' '}
          button as a fallback. YouTube and Vimeo links play directly inside the app.
        </p>

        <div className="flex flex-col gap-4">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
              <LinkIcon />
            </span>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Link"
              className="w-full h-14 pl-12 pr-4 rounded-full text-[15px] font-normal
                         border border-grey-500/40 dark:border-grey-500/30
                         bg-transparent text-text-primary placeholder:text-text-disabled
                         outline-none focus:border-primary-500 transition-[border-color] duration-150"
            />
          </div>

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
              <TextIcon />
            </span>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Text"
              maxLength={500}
              className="w-full h-14 pl-12 pr-4 rounded-full text-[15px] font-normal
                         border border-grey-500/40 dark:border-grey-500/30
                         bg-transparent text-text-primary placeholder:text-text-disabled
                         outline-none focus:border-primary-500 transition-[border-color] duration-150"
            />
          </div>
        </div>
      </div>
    </ModuleEditorBase>
  );
}
